import { Box, Button } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { useFeatureFlag } from "@hooks";
import { useAppLayout } from "@providers/AppLayoutProvider";
import { useMyself } from "@api/v1/hooks/useMyself";
import { Logo } from "@rentablo/components";
import { Profile } from "../Profile";

const Container = styled(Box)<{ collapsed: number }>`
  align-items: ${({ collapsed }) => (collapsed ? "center" : "flex-start")};
  gap: ${({ theme, collapsed }) => theme.spacing(collapsed ? 1 : 0.5)};
`;

const PlusTypography = styled(Box)`
  color: ${({ theme }) => theme.palette.common.black};
  background-color: ${({ theme }) => theme.palette.primary.light};
  font-weight: 600;
  font-size: 10px;
  padding: ${({ theme }) => ` ${theme.spacing(1)} ${theme.spacing(2)}`};
  border-radius: 6px;
  display: flex;
  align-items: center;
  height: 24px;
`;

const ProTypography = styled(PlusTypography)`
  background-color: ${({ theme }) =>
    theme.palette.button.upgradeButton?.default};
  color: ${({ theme }) => theme.palette.button.upgradeButton?.textOnDefault};
`;

export interface AppSidebarHeaderProps {
  onTestPremiumClick?: () => void;
  onLogoClick?: () => void;
  collapsed?: boolean;
}

export const AppSidebarHeader = ({
  onTestPremiumClick,
  onLogoClick,
}: AppSidebarHeaderProps) => {
  const { t } = useTranslation();
  const { data: myself } = useMyself();
  const isPremiumEnabled = useFeatureFlag("PREMIUM");

  const { sidebarCollapsed: collapsed } = useAppLayout();

  return (
    <Container
      display="flex"
      flexDirection="column"
      collapsed={collapsed ? 1 : 0}
      data-testid="header-container"
    >
      {collapsed && <Logo variant="superCompact" onClick={onLogoClick} />}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        {!collapsed && <Logo variant="sidebar" onClick={onLogoClick} />}
        {myself?.isUserPlus && isPremiumEnabled && !collapsed && (
          <PlusTypography>
            <span>PLUS</span>
          </PlusTypography>
        )}
        {myself?.isUserPro && isPremiumEnabled && !collapsed && (
          <Box px={3}>
            <ProTypography>
              <span>PRO</span>
            </ProTypography>{" "}
          </Box>
        )}
        {myself?.isUserFree && isPremiumEnabled && !collapsed && (
          <motion.div
            animate={{
              opacity: collapsed ? 0 : 1,
              transition: { delay: 0.15 },
            }}
          >
            {!collapsed && (
              <Button
                onClick={onTestPremiumClick}
                variant="primary"
                color="upgrade"
                size="small"
              >
                {t("sidebar.header.testPremium")}
              </Button>
            )}
          </motion.div>
        )}
      </Box>
      <Box width="100%">
        <Profile />
      </Box>
    </Container>
  );
};
